import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  LogInAction,
  passwordRecovery,
  UserInfo,
} from '../../layout/redux/layoutActions';

import {
  Button,
  Loading,
  IcoLoadingLogo,
  Alert,
  AddAlert,
} from '../../component';

import developerTitle from '../../assets/img/developer-title.svg';
import { IconVisible } from '../../component/icon/visible';
import { IconInvisible } from '../../component/icon/invisible';
import { shareSessioStorageBetweenTabs } from '../../helpers/manageStorage';

import './login.scss';

const LogIn = () => {
  const dispatch = useDispatch();
  const location = useLocation<{ from: { pathname: string } }>();
  const pathname = location.state?.from.pathname;
  const isTermsOfUse = pathname === '/terms-of-use';

  const { preference, load, user } = useSelector(
    (state: any) => state.layoutState
  );

  const nls = require(`./nls/${preference.language.id}.json`);
  const registerFormSchema = z.object({
    email: z
      .string()
      .min(1, {
        message: nls.error.login,
      })
      .regex(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/, {
        message: nls.error.mustBeAnEmail,
      }),
    password: z.string().min(1, {
      message: nls.error.password,
    }),
  });

  type LoginFormData = z.infer<typeof registerFormSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<LoginFormData>({
    resolver: zodResolver(registerFormSchema),
  });
  const [isVisiblePassword, setIsVisiblePassword] = useState<boolean>(false);

  const handleClickForgotPassword = () => {
    const email = getValues('email');

    if (errors.email) {
      dispatch(AddAlert('warning', 'Email inválido'));
    } else {
      const payload = {
        email: email,
      };
      dispatch(passwordRecovery(payload));
    }
  };

  useEffect(() => {
    if (isTermsOfUse && user?.user_id) {
      dispatch(UserInfo(user?.user_id, pathname));
    }
  }, [user?.user_id]);

  useEffect(() => {
    shareSessioStorageBetweenTabs();
  }, []);

  const onSubmit = (data: LoginFormData) => {
    dispatch(LogInAction(data, isTermsOfUse ? pathname : undefined));
  };

  const randomizedBackground = useMemo((): 'bg1' | 'bg2' | 'bg3' => {
    const randomizedNumber = Math.floor(Math.random() * 100);
    if (randomizedNumber < 34) {
      return 'bg1';
    }
    if (randomizedNumber >= 34 && randomizedNumber < 67) {
      return 'bg2';
    }
    return 'bg3';
  }, []);

  return (
    <div
      data-cy="LoginFullContainer"
      className={`box-login ${randomizedBackground}`}
    >
      {load ? <Loading icon={<IcoLoadingLogo cy="Loading" />} /> : null}
      <Alert />
      <div data-cy="LoginFormContainer">
        {
          <section data-cy="LoginFormSection">
            <img
              src={developerTitle}
              alt="developer"
              data-cy="LoginFormSection"
            />
            <h6 data-cy="LoginWelcomeText" className="welcome-text">
              {nls.information}
            </h6>
            <h6 data-cy="LoginCallToActionText" className="log-in-text">
              {nls.subInformation}
            </h6>
            <form onSubmit={handleSubmit(onSubmit)} data-cy="LoginForm">
              <div
                className="inputs-container"
                data-cy="LoginFormInputsContainer"
              >
                <div
                  className="input-and-error-message-container"
                  data-cy="FormErrorMessageContainer"
                >
                  <label data-cy="FormEmailLabel">{nls.email}</label>
                  <input
                    {...register('email')}
                    placeholder={nls.placeholder.email}
                    data-cy="FormEmailInput"
                  />
                  {errors.email && (
                    <p data-cy="FormErrorMessage">{errors.email.message}</p>
                  )}
                </div>
                <div
                  className="input-message-container"
                  data-cy="FormEmailInput"
                >
                  <label data-cy="FormPasswordLabel">{nls.password}</label>
                  <input
                    {...register('password')}
                    placeholder={nls.placeholder.password}
                    type={isVisiblePassword ? 'text' : 'password'}
                    data-cy="FormPasswordInput"
                  />
                  {errors.password && <p>{errors.password.message}</p>}
                  {isVisiblePassword ? (
                    <button
                      type="button"
                      className="visibility-icon-btn"
                      onClick={() => {
                        setIsVisiblePassword(false);
                      }}
                      data-cy="PasswordHiddenButton"
                    >
                      <IconVisible cy="PasswordHiddenIcon" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="visibility-icon-btn"
                      onClick={() => {
                        setIsVisiblePassword(true);
                      }}
                      data-cy="PasswordShowButton"
                    >
                      <IconInvisible cy="PasswordVisibleButton" />
                    </button>
                  )}
                </div>
              </div>
              <div
                className="forgot-password-container"
                data-cy="ForgotPasswordContainer"
              >
                <Button
                  cy="ForgotPasswordButton"
                  color="primary"
                  type="link"
                  variant="outline"
                  onClick={handleClickForgotPassword}
                  size="small"
                  style={{ height: '18px' }}
                >
                  <span data-cy="ForgotPasswordText">{nls.forgotPassword}</span>
                </Button>
              </div>

              <div className="box-btn-login" data-cy="SubmitFormContainer">
                <input
                  data-cy="SubmitButton"
                  type="submit"
                  className="submit-button"
                  value={nls.sigIn}
                  disabled={isSubmitting}
                />
              </div>
            </form>
          </section>
        }
        <small data-cy="CopyrightInformation">{nls.copyright}</small>
      </div>
    </div>
  );
};

export default LogIn;
